import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import CI from '../../assets/ci.svg';
import CI2 from '../../assets/ci2.svg';
import theme from '../../common/color/theme';
import { HorizontalEqualLayout, useResponsivePadding } from '../../common/common-layout';
import i18n from '../../common/i18n/i18n';
import { pxToRem } from '../../common/utils/ConvertUtils';
import { MULTIPLE_LANGUAGE, SHOW_DEV_MENU } from '../../config';
import '../../css/headermenu.css';
import { useDeviceType } from '../../hooks/useDeviceType';
import { dialogStore } from '../../store/DialogStore';
import menuStore from '../../store/MenuStore';
import { fetchAndUpdateTranslations } from './FetchAndUpdateTranslations';

const KOREAN_ONLY_STARTS_WITH = ['/promotion', '/customer'];
const KOREAN_ONLY_EXACT = ['/recruit', '/recruit/benefits', '/recruit/bbpeople'];

const isKoreanOnlyPage = (pathname: string) => {
  // ① 특정 prefix로 시작하는 경우
  const isStartsWithMatch = KOREAN_ONLY_STARTS_WITH.some((prefix) =>
    pathname.startsWith(prefix)
  );

  // ② 완전히 일치하는 경우
  const isExactMatch = KOREAN_ONLY_EXACT.includes(pathname);

  return isStartsWithMatch || isExactMatch;
};

const HeaderMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useDeviceType();
  const { horizontal: padding, width: contentWidth } = useResponsivePadding();

  const currentLangName = useMemo(() => {
    switch (i18n.language) {
      case 'ko':
        return 'KOR';
      case 'ko-mobile':
        return 'KOR';
      case 'en':
        return 'ENG';
      case 'en-mobile':
        return 'ENG';
      case 'zh':
        return 'CHN';
      case 'zh-mobile':
        return 'CHN';
      default:
        return 'KOR';
    }
  }, [i18n.language]);
  const currentLang = i18n.language;

  let menuName = '';
  let sectionName = '';
  if (location.pathname === '/') {
    menuName = '메인';
    sectionName = 'section0';
  } else if (location.pathname.startsWith('/introduce')) {
    menuName = '회사소개';
    sectionName = 'section1';
  } else if (location.pathname.startsWith('/business')) {
    menuName = '사업영역';
    sectionName = 'section2';
  } else if (location.pathname.startsWith('/promotion')) {
    menuName = '홍보채널';
    sectionName = 'section3';
  } else if (location.pathname.startsWith('/customer')) {
    menuName = '고객지원';
    sectionName = 'section4';
  } else if (location.pathname.startsWith('/recruit')) {
    menuName = '채용';
    sectionName = 'section5';
  }

  const [activeMenu, setActiveMenu] = useState<string>(menuName); // 기본 메뉴 선택
  const [activeSection, setActiveSection] = useState<string>(sectionName);
  const [isOpen, setIsOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false); // 메뉴 열릴때 상태 저장
  const [originalSection, setOriginalSection] = useState<string>(sectionName);

  const headerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isEng = ['en', 'en-mobile'].includes(i18n.language);
  const isChina = ['zh', 'zh-mobile'].includes(i18n.language);

  useEffect(() => {
    menuStore.setOpen(false);
  }, []);

  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      const target = event.relatedTarget as Node;
      if (
        headerRef.current &&
        menuRef.current &&
        !headerRef.current.contains(target) &&
        !menuRef.current.contains(target)
      ) {
        setActiveSection(originalSection);
        setIsOpen(false);
        menuStore.setOpen(false);
        //console.log("close:", saveOpen ? 'true' : 'false');

        if (isOpen) {
          menuStore.setDarkMode(saveOpen);
        }

        // menuStore.setBackgroundColor('transparent');
        // if (location.pathname === '/' || location.pathname === '/terms' || location.pathname === '/terms/policy') {
        //   menuStore.setDarkMode(false);
        // } else {
        //   menuStore.setDarkMode(true);
        // }

      }
    };

    const headerNode = headerRef.current;
    const menuNode = menuRef.current;

    // 두 영역에 이벤트 등록
    headerNode?.addEventListener('mouseleave', handleMouseLeave);
    menuNode?.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      headerNode?.removeEventListener('mouseleave', handleMouseLeave);
      menuNode?.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [saveOpen]);

  const handleMenuClick = (menu: string, href: string) => {
    var isMenuChanged = menuStore.currentSection !== href;
    setActiveMenu(menu);
    setActiveSection(href);

    if (!isMenuChanged) {
      setIsOpen(false);
      menuStore.setOpen(false);
      menuStore.hide();
      window.location.reload();
      return;
    }

    menuStore.setCurrentSection(href);

    if (href === '#section1') {
      menuStore.setDarkMode(false);
    } else {
      menuStore.setDarkMode(true);
    }

    if (href === '#section0') {
      navigate('/');
    } else if (href === '#section1') {
      navigate('/introduce/vision');
    } else if (href === '#section1-1') {
      navigate('/introduce/vision');
    } else if (href === '#section1-2') {
      navigate('/introduce/ceo');
    } else if (href === '#section1-3') {
      navigate('/introduce/history');
    } else if (href === '#section1-4') {
      navigate('/introduce/qualification');
    } else if (href === '#section1-5') {
      navigate('/introduce/organization');
    } else if (href === '#section1-6') {
      navigate('/introduce/ci');
    } else if (href === '#section1-7') {
      navigate('/introduce/location');
    } else if (href === '#section2') {
      navigate('/business');
    } else if (href === '#section2-1') {
      navigate('/business');
    } else if (href === '#section2-2') {
      navigate('/business/battery');
    } else if (href === '#section2-3') {
      navigate('/business/datacenter');
    } else if (href === '#section2-4') {
      navigate('/business/dryroom');
    } else if (href === '#section2-5') {
      navigate('/business/teflon');
    } else if (href === '#section2-6') {
      navigate('/business/cryogenic');
    } else if (href === '#section2-7') {
      navigate('/business/ac');
    } else if (href === '#section2-8') {
      navigate('/business/smartfab');
    } else if (href === '#section3') {
      navigate('/promotion');
    } else if (href === '#section3-1') {
      navigate('/promotion');
    } else if (href === '#section3-2') {
      navigate('/promotion/news');
    } else if (href === '#section4') {
      navigate('/customer/inquiry');
    } else if (href === '#section4-1') {
      navigate('/customer/inquiry');
    } else if (href === '#section4-2') {
      navigate('/customer/report');
    } else if (href === '#section5') {
      if (isEng || isChina) {
        navigate('/recruit/people');
      } else {
        navigate('/recruit');
      }
    } else if (href === '#section5-1') {
      if (isEng || isChina) {
        navigate('/recruit/people');
      } else {
        navigate('/recruit');
      }
    } else if (href === '#section5-2') {
      navigate('/recruit/people');
    } else if (href === '#section5-3') {
      navigate('/recruit/benefits');
    } else if (href === '#section5-4') {
      navigate('/recruit/bbpeople');
    } else {
      menuStore.setBackgroundColor('transparent');
    }
  };

  const getMargin = (ko: number, en: number, ja: number, zh: number): number => {
    switch (currentLang) {
      case 'en':
        return en;
      case 'ja':
        return ja;
      case 'zh':
        return zh;
      case 'ko':
      default:
        return ko;
    }
  };

  const menuData = [
    {
      name: t('회사소개'),
      gaName: '회사소개',
      href: '#section1',
      subs: [
        t('경영비전'),
        t('CEO 인사말'),
        t('회사연혁'),
        t('특허 및 인증'),
        t('조직도'),
        t('CI'),
        t('찾아오시는 길'),
      ],
      gaNames: [
        '경영비전',
        'CEO인사말',
        '회사연혁',
        '특허및인증',
        '조직도',
        'CI',
        '찾아오시는길',
      ]
    },
    {
      name: t('사업영역'),
      gaName: '사업영역',
      href: '#section2',
      subs: [
        t('반도체'),
        t('이차전지'),
        t('데이터센터'),
        t('클린룸/드라이룸'),
        t('테프론코팅'),
        t('극저온배관'),
      ],
      gaNames: [
        '반도체',
        '이차전지',
        '데이터센터',
        '클린룸드라이룸',
        '테프론코팅',
        '극저온배관',
      ]
    },
    ...(['ko', 'ko-mobile'].includes(currentLang)
      ? [{
        name: t('홍보채널'),
        gaName: '홍보채널',
        href: '#section3',
        subs: [
          t('미디어센터'),
          t('사내소식'),
        ],
        gaNames: [
          '미디어센터',
          '사내소식',
        ]
      }]
      : []
    ),
    ...(['ko', 'ko-mobile'].includes(currentLang)
      ? [{
        name: t('고객지원'),
        gaName: '고객지원',
        href: '#section4',
        subs: [
          t('문의하기'),
          t('BB열린소리'),
        ],
        gaNames: [
          '문의하기',
          'BB열린소리',
        ]
      }]
      : []
    ),
    (isEng || isChina) ? {
      name: t('채용'),
      gaName: '채용',
      href: '#section5',
      subs: [
        t('인재상'),
      ],
      gaNames: [
        '인재상',
      ]
    } : {
      name: t('채용'),
      gaName: '채용',
      href: '#section5',
      subs: [
        t('채용공고'),
        t('인재상'),
        t('복지제도'),
        t('직무소개'),
      ],
      gaNames: [
        '채용공고',
        '인재상',
        '복지제도',
        '직무소개',
      ]
    },
    ...(MULTIPLE_LANGUAGE
      ? [{
        name: currentLangName,
        gaName: currentLangName,
        href: '#section6',
        subs: [
          { label: t('ENG'), lang: 'en' },
          { label: t('CHN'), lang: 'zh' },
          { label: t('KOR'), lang: 'ko' },
          ...(SHOW_DEV_MENU
            ? [{ label: t('UPDATE'), lang: 'dev' }, { label: t('CLEAR'), lang: 'clear' }]
            : []
          ),
        ],
      }]
      : []
    ),
  ];



  const logo = menuStore.isDarkMode ? CI2 : CI;
  return (
    <>
      <header
        ref={headerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100px',
          backgroundColor: 'transparent',
          color: menuStore.isDarkMode ? 'white' : 'black',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
          zIndex: 1000,
          paddingRight: '4px',
          borderBottom: isOpen
            ? '1px solid #F0F1F2'
            : '1px solid #ffffff88',
        }}
      >
        <img src={logo}
          style={{ marginLeft: padding, marginTop: '39px', cursor: 'pointer', width: pxToRem(225), height: pxToRem(32) }}
          onClick={() => {
            navigate('/');
            menuStore.setCurrentSection('#section0')
          }}
          data-analytics-name="메인_로고"
        />
        <nav
          onMouseEnter={() => {
            if (!isOpen) {
              setIsOpen(true);
              menuStore.setOpen(true);
              //console.log("open:", menuStore.isDarkMode ? 'true' : 'false');
              setSaveOpen(menuStore.isDarkMode);
              menuStore.setBackgroundColor('white');
              menuStore.setDarkMode(false);
            }
          }}
          style={{ marginTop: '32px' }}
        >
          <HorizontalEqualLayout className="menu" width={pxToRem(isEng ? 1200 : 1100)} style={{ marginRight: `${isDesktop ? padding - (isEng ? 245 : isChina ? 222 : 138) : padding - (isEng ? 185 : 104)}px` }}>
            {menuData.map((menu) => (
              <div
                key={menu.name}
                className={`menu-item ${`#${activeSection.replace('#', '')}` === menu.href.replace(/-\d+$/, '') ? 'active' : ''}`}
                onMouseEnter={() => {
                  // if (menu.href !== '#section6') {
                  //   setActiveSection(menu.href);
                  // }
                }}
                style={{
                  marginLeft: '0',
                  textAlign: 'left',
                  cursor: 'default',
                  fontFamily: isChina ? theme.typography.fontFamily : 'LINE Seed Sans KR'
                }}
              >
                <a
                  href={menu.href}
                  style={{
                    color: menuStore.isDarkMode ? 'white' : 'black',
                    fontFamily: isChina ? theme.typography.fontFamily : 'LINE Seed Sans KR',
                    ...isEng ? { fontSize: pxToRem(26) } : {}
                  }}
                  data-analytics-name={`메뉴_${menu.gaName || ''}`}
                >
                  {menu.name}
                </a>
              </div>
            ))}
          </HorizontalEqualLayout>
        </nav>
      </header>

      <div
        ref={menuRef}
        className={`menu-content ${isOpen ? 'menu-content-open' : ''}`}
        style={{
          backgroundColor: menuStore.backgroundColor, paddingTop: '90px', paddingBottom: isDesktop ? '6rem' : '2rem', zIndex: 999, display: 'flex',
          justifyContent: 'flex-end',
          fontFamily: isChina ? theme.typography.fontFamily : 'LINE Seed Sans KR'
        }}
      >
        <HorizontalEqualLayout className="menu-grid" width={pxToRem(isEng ? 1200 : 1100)} style={{ alignItems: 'start', marginRight: `${isDesktop ? padding - (isEng ? 245 : isChina ? 222 : 138) : padding - (isEng ? 185 : 104)}px` }}>
          {menuData.map((menu) => (
            <div key={menu.name} style={{ paddingTop: '18px' }}>
              <ul
                style={{
                  marginLeft: '0',
                  textAlign: 'left',
                  fontFamily: isChina ? theme.typography.fontFamily : 'LINE Seed Sans KR',
                }}
              >
                {menu.subs.map((sub, index) => {
                  const subHref = `${menu.href}-${index + 1}`;

                  if (typeof sub === 'object' && 'lang' in sub) {
                    if (currentLangName !== sub.label) {
                      // 언어 변경 항목
                      return (
                        <li key={index} style={{ height: '2.8rem' }}>
                          <a
                            href="#"
                            style={{
                              color: menuStore.isDarkMode ? '#e6e6e6' : '#464646',
                              fontFamily: isChina ? theme.typography.fontFamily : 'LINE Seed Sans KR',
                              ...isEng ? { fontSize: pxToRem(20) } : {}
                            }}
                            onClick={async (e) => {
                              e.preventDefault();
                              const targetLang = sub.lang;
                              const isTargetKorean = targetLang === 'ko' || targetLang === 'ko-mobile';
                              const isCurrentKorean = ['ko', 'ko-mobile'].includes(i18n.language);

                              if (!isTargetKorean && isCurrentKorean && isKoreanOnlyPage(location.pathname)) {
                                let title = '';
                                let message = '';
                                let confirmButtonTitle = '';
                                switch (targetLang) {
                                  case 'en':
                                  case 'en-mobile':
                                    title = 'Notice';
                                    message = 'This page is only available in Korean.';
                                    confirmButtonTitle = 'OK';
                                    break;
                                  case 'zh':
                                  case 'zh-mobile':
                                    title = '提示';
                                    message = '此页面仅提供韩语版本。';
                                    confirmButtonTitle = '确认';
                                    break;
                                  default:
                                    title = '알림';
                                    message = '해당 페이지는 한국어에서만 제공됩니다.';
                                    confirmButtonTitle = '확인';
                                    break;
                                }

                                dialogStore.showDialog({
                                  title: title,
                                  message,
                                  confirmButtonTitle: confirmButtonTitle,
                                  isCaution: false,
                                  onClose: () => {
                                    i18n.changeLanguage(targetLang);
                                    navigate('/');
                                  },
                                  onConfirm: () => {
                                    i18n.changeLanguage(targetLang);
                                    navigate('/');
                                  }
                                });

                                return;

                                // const confirmed = window.confirm(
                                //   '현재 페이지는 한국어에서만 제공됩니다.\n홈으로 이동한 후 선택하신 언어로 변경하시겠습니까?',
                                // );
                                // if (!confirmed) {
                                //   return;
                                // }
                                // navigate('/');
                              }

                              if (SHOW_DEV_MENU) {
                                if (sub.lang === 'dev') {
                                  fetchAndUpdateTranslations();
                                } else if (sub.lang === 'clear') {
                                  localStorage.removeItem("translations");
                                  window.location.reload();
                                } else {
                                  // 언어 변경
                                  i18n.changeLanguage(targetLang);
                                  window.location.reload();
                                }
                              } else {
                                i18n.changeLanguage(targetLang);
                              }
                            }}
                            data-analytics-name={`메뉴_${sub.label}`}
                          >
                            {sub.label}
                          </a>
                        </li>
                      );
                    } else {
                      return (<div></div>)
                    }
                  }

                  // 일반 메뉴 항목 (문자열인 경우)
                  return (
                    <li key={index} style={{
                      height: '2.8rem',
                      marginTop: subHref === '#section2-7' ? `-${pxToRem(7)}` : undefined,
                      marginBottom: subHref === '#section2-7' ? '16px' : undefined
                    }}>
                      <a
                        href={subHref}
                        style={{
                          color: menuStore.isDarkMode ? '#e6e6e6' : menuStore.currentSection === subHref ? theme.colors.BIBI : '#464646', lineHeight: 1,
                          fontFamily: isChina ? theme.typography.fontFamily : 'LINE Seed Sans KR',
                          ...isEng ? { fontSize: pxToRem(20) } : {}
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleMenuClick(menu.name, subHref);
                        }}
                        onMouseEnter={() => setActiveSection(menu.href)}
                        data-analytics-name={`메뉴_${menu.gaNames?.[index] || ''}`}
                      >
                        {sub}
                      </a>
                    </li>
                  );
                })}

              </ul>
            </div>
          ))}
        </HorizontalEqualLayout>
      </div>
    </>
  );
};

export default observer(HeaderMenu);
